import React, { useState, createContext, useContext, useEffect, useMemo, useRef } from 'react';
import io from 'socket.io-client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from './Components/Toaster/Toaster';
import ConfirmationPopup from './Components/ModalPopup/ConfirmationPopup';
import DuplicateConfirmModal from './Components/POUploadNotifications/DuplicateConfirmModal';
import SupplierUploadErrorModal from './Components/POUploadNotifications/SupplierUploadErrorModal';
import SupplierDuplicateConfirmModal from './Components/POUploadNotifications/SupplierDuplicateConfirmModal';
import * as ProductListAction from './Pages/RangePlan/ProductList/store/ProductList.action';
import * as ProductPurchaseOrderTodoAction from './Pages/Dashboard/TodoList/ProductPurchaseOrderTodo/store/productPurchaseOrderTodo.action';
import { getSubmitDownloadRequest } from './services/submit-download.service';
import {
  forgetPendingDownload,
  listPendingDownloads,
  isPendingInThisTab,
} from './Pages/DevlopmentOrder/SubmitDownload/pending-downloads';
import { submitDownloadFailureMessage } from './Pages/DevlopmentOrder/SubmitDownload/submit-download-failure';
import { useLengthLabel } from './hooks/useLengthLabel';
import {
  buildKeyFieldConfirmation,
  buildKeyFieldConfirmPayload,
  forgetApprovedDuplicates,
  readApprovedDuplicates,
  rememberApprovedDuplicates,
  resolveCancelErrorSheet,
  resolveUploadPrompt,
  resolveKeyFieldConfirmationAfterDuplicates,
  runSingleFlight,
} from './Pages/RangePlan/ProductList/rangePlanUploadPrompt';
import { resolveResultEventId, startFocusedTabResultRouter } from './utils/focused-tab-result-router';

function getCareErrorSheetStableKey(resolvedUrlStr) {
  try {
    const parsedUrl = new URL(resolvedUrlStr, window.location.origin);
    const s3Key = parsedUrl.searchParams.get('s3Key');
    if (s3Key) return decodeURIComponent(s3Key);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return resolvedUrlStr;
  }
}

function getCareErrorSheetLockName(resolvedUrlStr) {
  const stable = getCareErrorSheetStableKey(resolvedUrlStr);
  const trimmed = stable.length > 220 ? stable.slice(-220) : stable;
  return `plm-care-error-sheet:${encodeURIComponent(trimmed)}`.slice(0, 250);
}

const CARE_ERROR_SHEET_DEDUPE_PREFIX = 'plm:careErrorSheetDownloaded:';
const CARE_ERROR_SHEET_DEDUPE_TTL_MS = 10 * 60 * 1000;

function readCareErrorSheetSentinel(stableKey) {
  try {
    const raw = localStorage.getItem(CARE_ERROR_SHEET_DEDUPE_PREFIX + stableKey);
    if (!raw) return false;
    const ts = Number.parseInt(raw, 10);
    if (!Number.isFinite(ts) || Date.now() - ts > CARE_ERROR_SHEET_DEDUPE_TTL_MS) {
      localStorage.removeItem(CARE_ERROR_SHEET_DEDUPE_PREFIX + stableKey);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function writeCareErrorSheetSentinel(stableKey) {
  try {
    localStorage.setItem(CARE_ERROR_SHEET_DEDUPE_PREFIX + stableKey, String(Date.now()));
  } catch {
    // localStorage unavailable — fall back to in-memory dedupe only.
  }
}

const PENDING_CARE_KEY = 'plm:pendingCareUploads:v1';
const PENDING_CARE_TTL_MS = 30 * 60 * 1000;
const pendingCareUploadSessions = new Map();
const PENDING_TESTING_KEY = 'plm:pendingTestingUploads:v1';
const PENDING_TESTING_TTL_MS = 30 * 60 * 1000;
const pendingTestingUploadSessions = new Map();
let currentSocketId = null;

function persistPendingCareUploads() {
  try {
    sessionStorage.setItem(PENDING_CARE_KEY, JSON.stringify(Array.from(pendingCareUploadSessions.entries())));
  } catch (error) {
    // sessionStorage unavailable or quota exceeded — keep in-memory state
    // only. The originator-only guarantee still holds for the active session;
    // we simply lose the reload-survival property. Same as pre-storage.
    console.error('Failed to persist pending care uploads:', error);
  }
}

function prunePendingCareUploads() {
  const now = Date.now();
  let pruned = false;
  for (const [id, ts] of pendingCareUploadSessions) {
    if (now - ts >= PENDING_CARE_TTL_MS) {
      pendingCareUploadSessions.delete(id);
      pruned = true;
    }
  }
  if (pruned) persistPendingCareUploads();
}

(function hydratePendingCareUploads() {
  try {
    const raw = sessionStorage.getItem(PENDING_CARE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    if (!Array.isArray(entries)) return;
    const now = Date.now();
    for (const item of entries) {
      if (!Array.isArray(item) || item.length !== 2) continue;
      const [id, ts] = item;
      if (typeof id !== 'string' || typeof ts !== 'number') continue;
      if (now - ts < PENDING_CARE_TTL_MS) {
        pendingCareUploadSessions.set(id, ts);
      }
    }

    if (pendingCareUploadSessions.size !== entries.length) {
      persistPendingCareUploads();
    }
  } catch (error) {
    // Corrupt or unavailable storage — start clean.
    console.error('Failed to hydrate pending care uploads:', error);
  }
})();

(function hydratePendingTestingUploads() {
  try {
    const raw = sessionStorage.getItem(PENDING_TESTING_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    if (!Array.isArray(entries)) return;
    const now = Date.now();
    for (const item of entries) {
      if (!Array.isArray(item) || item.length !== 2) continue;
      const [id, ts] = item;
      if (typeof id !== 'string' || typeof ts !== 'number') continue;
      if (now - ts < PENDING_TESTING_TTL_MS) {
        pendingTestingUploadSessions.set(id, ts);
      }
    }
  } catch (error) {
    console.error('Failed to hydrate pending testing uploads:', error);
  }
})();

export function registerCareUpload(uploadSessionId) {
  if (!uploadSessionId) return;
  prunePendingCareUploads();
  pendingCareUploadSessions.set(uploadSessionId, Date.now());
  persistPendingCareUploads();
}

export function registerTestingUpload(uploadSessionId) {
  if (!uploadSessionId) return;
  pendingTestingUploadSessions.set(uploadSessionId, Date.now());
  try {
    sessionStorage.setItem(PENDING_TESTING_KEY, JSON.stringify(Array.from(pendingTestingUploadSessions.entries())));
  } catch (error) {
    console.error('Failed to persist pending testing uploads:', error);
  }
}

export function getCurrentSocketId() {
  return currentSocketId;
}

function getErrorSheetLockName(resolvedUrlStr) {
  try {
    const parsedUrl = new URL(resolvedUrlStr, window.location.origin);
    const s3Key = parsedUrl.searchParams.get('s3Key');
    const stable = s3Key ? decodeURIComponent(s3Key) : resolvedUrlStr;
    const trimmed = stable.length > 220 ? stable.slice(-220) : stable;
    return `plm-error-sheet:${encodeURIComponent(trimmed)}`.slice(0, 250);
  } catch {
    return 'plm-error-sheet:fallback';
  }
}

export const StateContext = createContext(null);

export default function SocketProvider({ children }) {
  function getChatServerURL() {
    const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;
    const forceLocal = process.env.REACT_APP_FORCE_LOCAL_SOCKET === 'true';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    const slugName = window.location.hostname?.split('.')?.[0];
    const protocol = process.env.REACT_APP_PROTOCOL || 'http://';

    let socketURL;
    if (forceLocal || isLocalhost || isDevelopment) {
      const port = process.env.REACT_APP_PORT || '8014';
      socketURL = `${protocol}localhost:${port}/`;
    } else {
      socketURL = `${protocol}${slugName}${process.env.REACT_APP_API_URL}/`;
    }
    return socketURL;
  }

  const Config = {
    socketConnectionURL: getChatServerURL(),
  };

  const initialSetting = {
    socketContext: null,
  };
  const [socketState, setSocket] = useState(initialSetting);
  const [dupModalOpen, setDupModalOpen] = useState(false);
  const [dupData, setDupData] = useState(null);
  const [keyFieldConfirmation, setKeyFieldConfirmation] = useState(null);
  const rangePlanResultRouterRef = useRef(null);
  const rangePlanResultPresenterRef = useRef(null);
  const [poDuplicateModal, setPoDuplicateModal] = useState({
    show: false,
    data: null,
  });
  const [supplierErrorModal, setSupplierErrorModal] = useState({
    show: false,
    data: null,
  });
  const [supplierDuplicateModal, setSupplierDuplicateModal] = useState({
    show: false,
    data: null,
  });
  const isConfirmingSupplierUpload = useRef(false);

  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  /**
   * Fetches a finished submit-download and hands it to the browser.
   *
   * The link is asked for at this moment rather than carried on the event, so it is freshly
   * presigned and cannot have expired waiting for a backgrounded tab to come back.
   *
   * @param {string} requestId - the finished request.
   * @returns {Promise<boolean>} true once the file has been offered to the user.
   */
  const deliverSubmitDownload = async (requestId) => {
    try {
      const res = await getSubmitDownloadRequest(requestId);
      const zipUrl = res?.data?.zipUrl;
      if (res?.data?.status !== 'Completed' || !zipUrl) {
        return false;
      }
      forgetPendingDownload(requestId);
      const fileUrl = zipUrl.startsWith('http') ? zipUrl : `${imageUrl}/${zipUrl}`;
      const response = await fetch(fileUrl);
      if (!response.ok) {
        toasterMsg(t('DEVLOPMENT_ORDER.SUBMIT_DOWNLOAD.SUBMIT_DOWNLODS_PRODUCT.DOWNLOAD_FAILED'), 'danger');
        return false;
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = res?.data?.fileName || 'techpack.zip';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toasterMsg(t('DEVLOPMENT_ORDER.SUBMIT_DOWNLOAD.SUBMIT_DOWNLODS_PRODUCT.DOWNLOAD_SUCCESS'), 'success');
      return true;
    } catch (error) {
      toasterMsg(t('DEVLOPMENT_ORDER.SUBMIT_DOWNLOAD.SUBMIT_DOWNLODS_PRODUCT.DOWNLOAD_FAILED'), 'danger');
      return false;
    }
  };

  /**
   * Picks up downloads this tab asked for but never heard back about.
   *
   * A socket event only reaches a connected tab, so one that was reloaded, backgrounded or
   * briefly offline would otherwise wait on a file already sitting in S3. Runs on connect and
   * on reconnect, which is exactly when an event could have been missed.
   *
   * A request still running is left alone; its own event will arrive. One the server no longer
   * knows for this user (404) is dropped. Any other failure keeps the id for the next reconnect
   * and is reported once per pass, not once per download.
   *
   * @returns {Promise<void>} resolves once every pending request has been checked.
   */
  const recoverPendingSubmitDownloads = async () => {
    const pending = listPendingDownloads();
    let recoveryFailed = false;
    for (const requestId of pending) {
      try {
        const res = await getSubmitDownloadRequest(requestId);
        const status = res?.data?.status;
        if (status === 'Completed') {
          await deliverSubmitDownload(requestId);
        } else if (status === 'Failed' || !status) {
          forgetPendingDownload(requestId);
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          forgetPendingDownload(requestId);
        } else {
          recoveryFailed = true;
        }
      }
    }
    if (recoveryFailed) {
      toasterMsg(t('DEVLOPMENT_ORDER.SUBMIT_DOWNLOAD.SUBMIT_DOWNLODS_PRODUCT.DOWNLOAD_FAILED'), 'danger');
    }
  };
  const { t: tPO } = useTranslation('purchase-order');
  const lengthLabel = useLengthLabel();
  const { t: tSupplier } = useTranslation('modules/partner-overview');
  const { t: tDashboard } = useTranslation('modules/dashboard');
  const { t: tRangePlanIdentifier } = useTranslation('rangePlanProductIdentifier');

  const downloadedErrorSheets = useRef(new Set());
  const isMovingFile = useRef(false);
  const processedPOEvents = useRef(new Set());
  const processedPCPEvents = useRef(new Set());
  const lambdaTriggerInProgress = useRef(false);
  const poEventDebounceTimers = useRef(new Map());
  const pcpEventDebounceTimers = useRef(new Map());
  const modalJustClosed = useRef(false);
  const isPOModalShowing = useRef(false);
  const processedRPCustomSectionEvents = useRef(new Set());
  const processedRPCareUploadEvents = useRef(new Set());
  const rangePlanCareUploadEventDebounceTimers = useRef(new Map());
  const rangePlanTestingUploadEventDebounceTimers = useRef(new Map());
  const rpCustomSectionEventDebounceTimers = useRef(new Map());

  /**
   * Downloads an error sheet for PO / PCP / RP-custom-section flows.
   *
   * These error sheets are served from our own backend proxy (same-origin),
   * so credentials are included. Dedupe uses the in-memory ref plus a 24h
   * localStorage record to prevent duplicate downloads on replayed events.
   *
   * Keep this function stable — it's shared by multiple upload features.
   */
  const downloadErrorSheet = async (errorSheetUrl) => {
    const resolvedUrl = typeof errorSheetUrl === 'string' && errorSheetUrl.startsWith('/') ? `${window.location.origin}${errorSheetUrl}` : errorSheetUrl;

    if (downloadedErrorSheets.current.has(resolvedUrl)) {
      return;
    }

    const DOWNLOADED_ERROR_SHEETS_KEY = 'downloadedErrorSheets';
    const DOWNLOADED_SHEETS_TTL_MS = 24 * 60 * 60 * 1000;

    const getDownloadedSheets = () => {
      try {
        const stored = localStorage.getItem(DOWNLOADED_ERROR_SHEETS_KEY);
        if (!stored) return new Set();
        const data = JSON.parse(stored);
        const now = Date.now();
        const validEntries = data.filter((entry) => now - entry.timestamp < DOWNLOADED_SHEETS_TTL_MS);
        return new Set(validEntries.map((entry) => entry.url));
      } catch {
        return new Set();
      }
    };

    const markSheetAsDownloaded = (url) => {
      try {
        const stored = localStorage.getItem(DOWNLOADED_ERROR_SHEETS_KEY);
        const data = stored ? JSON.parse(stored) : [];
        const now = Date.now();
        const validEntries = data.filter((entry) => now - entry.timestamp < DOWNLOADED_SHEETS_TTL_MS);
        validEntries.push({ url, timestamp: now });
        localStorage.setItem(DOWNLOADED_ERROR_SHEETS_KEY, JSON.stringify(validEntries));
      } catch (error) {
        console.error('Failed to mark error sheet as downloaded:', error);
      }
    };

    const getFilenameFromContentDisposition = (contentDisposition) => {
      if (!contentDisposition) return null;
      const filenameMatch =
        contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) || contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
      return filenameMatch ? filenameMatch[1].trim() : null;
    };

    const getFilenameFromUrl = (urlStr) => {
      try {
        const url = new URL(urlStr, window.location.origin);
        const s3Key = url.searchParams.get('s3Key');
        if (s3Key) {
          const parts = decodeURIComponent(s3Key).split('/');
          const last = parts[parts.length - 1];
          if (last && (last.endsWith('.xlsx') || last.endsWith('.xls'))) return last;
        }
        const pathParts = url.pathname.split('/');
        return pathParts[pathParts.length - 1] || 'error-sheet.xlsx';
      } catch {
        return 'error-sheet.xlsx';
      }
    };

    const downloadedSheets = getDownloadedSheets();
    if (downloadedSheets.has(resolvedUrl)) {
      return;
    }

    try {
      downloadedErrorSheets.current.add(resolvedUrl);

      const response = await fetch(resolvedUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          toasterMsg('Error sheet is no longer available.', 'danger');
          return;
        }
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }

      const runDownloadErrorSheet = async () => {
        const filename = getFilenameFromContentDisposition(response.headers.get('Content-Disposition')) || getFilenameFromUrl(resolvedUrl);

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = filename;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        setTimeout(() => {
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);

        markSheetAsDownloaded(resolvedUrl);
      };

      const lockName = getErrorSheetLockName(resolvedUrl);
      const { locks } = navigator;

      if (locks?.request) {
        await locks.request(lockName, { ifAvailable: true }, async (lock) => {
          if (lock == null) {
            return;
          }
          await runDownloadErrorSheet();
        });
        return;
      }

      if (document.visibilityState !== 'visible') {
        return;
      }

      await runDownloadErrorSheet();
    } catch (error) {
      downloadedErrorSheets.current.delete(resolvedUrl);
      const isNetworkOrFetch = error?.message?.includes('404') || error?.message?.includes('Failed to fetch') || error?.name === 'TypeError';

      if (isNetworkOrFetch) {
        toasterMsg('Error sheet could not be downloaded. Please try again or contact support.', 'danger');
        return;
      }

      try {
        const fallbackResponse = await fetch(resolvedUrl, {
          method: 'GET',
          mode: 'cors',
          credentials: 'include',
        });

        if (fallbackResponse.ok) {
          const fallbackFilename = getFilenameFromContentDisposition(fallbackResponse.headers.get('Content-Disposition')) || getFilenameFromUrl(resolvedUrl);
          const blob = await fallbackResponse.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = downloadUrl;
          anchor.download = fallbackFilename;
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
          setTimeout(() => {
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(downloadUrl);
          }, 100);
          markSheetAsDownloaded(resolvedUrl);
          toasterMsg('Error sheet downloaded successfully', 'success');
        } else {
          toasterMsg('Error sheet could not be downloaded. Please try again or contact support.', 'danger');
        }
      } catch (fallbackError) {
        toasterMsg('Error sheet could not be downloaded. Please try again or contact support.', 'danger');
      }
    }
  };

  /**
   * Downloads a care-upload error sheet.
   *
   * Care-upload error sheets arrive as cross-origin presigned S3 URLs from
   * the Lambda, so this function:
   *   1. Fetches with `credentials: 'omit'` to avoid CORS preflight failures.
   *   2. Uses a `blob:` URL for the anchor click to avoid Chrome's
   *      "insecure download" warning on cross-origin direct links.
   *   3. Serializes cross-tab downloads via the Web Locks API (exclusive
   *      lock, keyed on the S3 object key) and persists a short-lived
   *      localStorage sentinel so only one tab on the device performs the
   *      download when the same event fans out to multiple tabs.
   *
   * Kept intentionally separate from `downloadErrorSheet` so behaviour
   * changes here never regress the PO / PCP / RP-custom-section flows.
   */
  const downloadCareErrorSheet = async (errorSheetUrl) => {
    if (!errorSheetUrl) return;
    const resolvedUrl = typeof errorSheetUrl === 'string' && errorSheetUrl.startsWith('/') ? `${window.location.origin}${errorSheetUrl}` : errorSheetUrl;

    if (downloadedErrorSheets.current.has(resolvedUrl)) {
      return;
    }

    const stableKey = getCareErrorSheetStableKey(resolvedUrl);
    const lockName = getCareErrorSheetLockName(resolvedUrl);

    const isCrossOrigin = (() => {
      try {
        return new URL(resolvedUrl).origin !== window.location.origin;
      } catch {
        return false;
      }
    })();

    const getFilenameFromContentDisposition = (cd) => {
      if (!cd) return null;
      const filenameMatch = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) || cd.match(/filename=["']?([^"';]+)["']?/i);
      return filenameMatch ? decodeURIComponent(filenameMatch[1].trim()) : null;
    };

    const getFilenameFromUrl = () => {
      try {
        const url = new URL(resolvedUrl);
        const s3Key = url.searchParams.get('s3Key');
        if (s3Key) {
          const parts = decodeURIComponent(s3Key).split('/');
          const last = parts[parts.length - 1];
          if (last && (last.endsWith('.xlsx') || last.endsWith('.xls'))) return last;
        }
        const parts = url.pathname.split('/');
        return parts[parts.length - 1] || 'error-sheet.xlsx';
      } catch {
        return 'error-sheet.xlsx';
      }
    };

    const runDownload = async () => {
      const response = await fetch(resolvedUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: isCrossOrigin ? 'omit' : 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const filename = getFilenameFromContentDisposition(response.headers.get('Content-Disposition')) || getFilenameFromUrl();

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      setTimeout(() => {
        anchor.remove();
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    };

    const downloadOnceAcrossTabs = async () => {
      if (readCareErrorSheetSentinel(stableKey)) {
        downloadedErrorSheets.current.add(resolvedUrl);
        return;
      }
      downloadedErrorSheets.current.add(resolvedUrl);
      try {
        await runDownload();
        writeCareErrorSheetSentinel(stableKey);
      } catch (error) {
        console.error('[downloadCareErrorSheet] Failed:', error);
        downloadedErrorSheets.current.delete(resolvedUrl);
        toasterMsg('Error sheet could not be downloaded. Please try again or contact support.', 'danger', 6000);
      }
    };

    if (navigator.locks?.request) {
      await navigator.locks.request(lockName, downloadOnceAcrossTabs);
      return;
    }

    await downloadOnceAcrossTabs();
  };

  useEffect(() => {
    const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserInfo'));
    if (!loggedInUserInfo) {
      return;
    }
    const { userId } = loggedInUserInfo || {};
    const supplierUserId = loggedInUserInfo?.supplierUserId ? loggedInUserInfo.supplierUserId : '';
    const host = window.location.hostname;
    const company = host.split('.')[0];
    if (loggedInUserInfo && userId) {
      const newSocket = io(Config.socketConnectionURL, {
        query: {
          token: loggedInUserInfo?.token,
          userId: supplierUserId !== '' ? supplierUserId : userId,
          supplierUserId,
          role: loggedInUserInfo?.role,
          company,
        },
        path: '/api/socket.io',
        transports: ['websocket'],
        auth: {
          token: loggedInUserInfo?.token,
          userId,
          role: loggedInUserInfo?.role,
          company,
          supplierUserId,
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
      });
      setSocket((prevState) => ({ ...prevState, socketContext: newSocket }));

      const requestCareUploadReplay = () => {
        if (pendingCareUploadSessions.size === 0) return;
        newSocket.emit('care-upload-replay-query', Array.from(pendingCareUploadSessions.keys()));
      };
      const requestTestingUploadReplay = () => {
        if (pendingTestingUploadSessions.size === 0) return;
        newSocket.emit('testing-upload-replay-query', Array.from(pendingTestingUploadSessions.keys()));
      };

      newSocket.off('connect').on('connect', () => {
        currentSocketId = newSocket.id;
        console.log('Socket connected:', {
          socketId: newSocket.id,
          userId: supplierUserId !== '' ? supplierUserId : userId,
          supplierUserId,
          company,
          role: loggedInUserInfo?.role,
        });
        requestCareUploadReplay();
        requestTestingUploadReplay();
        recoverPendingSubmitDownloads();
      });
      newSocket.off('disconnect').on('disconnect', (data) => {
        console.log('Socket disconnected:', data);
      });
      newSocket.off('reconnect').on('reconnect', (data) => {
        console.log('Socket reconnected:', data);
        requestCareUploadReplay();
        requestTestingUploadReplay();
        recoverPendingSubmitDownloads();
      });
      newSocket.off('connect_error').on('connect_error', (err) => {
        console.error('Socket connection error:', err);
      });
      newSocket.off('error').on('error', () => {});

      newSocket.off('product-listing-custom-section-success').on('product-listing-custom-section-success', function success() {
        toasterMsg('updated successfully', 'success');
      });
      newSocket.off('user-rights-updated-success').on('user-rights-updated-success', function failure() {});

      /* The server accepts a submit-download and answers before it has run, so the finished
      archive arrives here rather than on the response to the click. The event carries no link —
      the status endpoint presigns one on read, so a link cannot go stale sitting in a row. */
      newSocket
        .off('submit-download-ready')
        .on('submit-download-ready', async function submitDownloadReady(data) {
          if (!isPendingInThisTab(data?.requestId)) {
            return;
          }
          forgetPendingDownload(data.requestId);
          if (data.status !== 'Completed') {
            const failure = submitDownloadFailureMessage(data);
            toasterMsg(t(failure.key, failure.options), 'danger');
            return;
          }
          await deliverSubmitDownload(data.requestId);
        });

      newSocket.off('todo-pdf-ready').on('todo-pdf-ready', async function todoPdfReady(data) {
        if (data?.error) {
          toasterMsg(tDashboard('DASHBOARD.TODO_PDF.GENERATION_FAILED'), 'danger');
          return;
        }
        const fileUrl = data.zipUrl?.startsWith('http') ? data.zipUrl : `${imageUrl}/${data.zipUrl}`;
        const fileName = data.fileName || 'report.pdf';
        fetch(fileUrl)
          .then((response) => response.blob())
          .then((blob) => {
            const downloadUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = downloadUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toasterMsg(tDashboard('DASHBOARD.TODO_PDF.DOWNLOAD_SUCCESS'), 'success');
          })
          .catch((error) => {
            console.error('PDF download error:', error);
            toasterMsg(tDashboard('DASHBOARD.TODO_PDF.DOWNLOAD_FAILED'), 'danger');
          });
      });

      newSocket.off('lambda-callback-success').on('lambda-callback-success', function success(data) {
        if (data?.notificationType === 'pim-download') return;
        offerRangePlanResult('lambdaCallback', 'lambda-callback-success', data);
      });

      newSocket.off('rp-default-options-completed').on('rp-default-options-completed', (data) => {
        offerRangePlanResult('rpDefaultOptions', 'rp-default-options-completed', data);
      });

      newSocket.off('rp-identifier-barcodes-completed').on('rp-identifier-barcodes-completed', (data) => {
        offerRangePlanResult('rpIdentifierBarcodes', 'rp-identifier-barcodes-completed', data);
      });

      /**
       * Handles PO upload processing notification (shows when Lambda starts processing)
       * This gives immediate feedback to the user that their file is being processed
       */
      newSocket.off('po-upload-processing').on('po-upload-processing', () => {
        console.log('PO upload processing');
      });

      newSocket.off('po-upload-success').on('po-upload-success', (data) => {
        toasterMsg('File processed successfully', 'success');
        const isOnTodoPage = window.location.pathname.includes('/todo-list/product-purchase-order-todo');
        if (isOnTodoPage) {
          window.dispatchEvent(new CustomEvent('po-list-refresh'));
        }
      });

      /**
       * Handles PCP upload processed (PO-based and product-based: debounce, dedupe, download error sheet)
       * Uses stable eventId to prevent duplicate notifications and duplicate downloads
       */
      const handlePCPUploadProcessed = (data) => {
        const currentUserId = loggedInUserInfo?.supplierUserId || loggedInUserInfo?.userId;
        const payloadUserId = data?.data?.userId || data?.userId;
        if (payloadUserId && currentUserId && payloadUserId.toString() !== currentUserId.toString()) {
          return;
        }

        const errorSheetKey = data?.errorSheet?.key;
        const pcpId = data?.data?.pcpId ?? data?.pcpId;
        const fallbackId = `pcp_${pcpId ?? ''}_${userId ?? ''}` || `pcp_${Date.now()}`;
        const eventId = errorSheetKey ?? fallbackId;

        if (processedPCPEvents.current.has(eventId)) {
          return;
        }

        if (pcpEventDebounceTimers.current.has(eventId)) {
          clearTimeout(pcpEventDebounceTimers.current.get(eventId));
        }

        const debounceTimer = setTimeout(() => {
          if (processedPCPEvents.current.has(eventId)) {
            pcpEventDebounceTimers.current.delete(eventId);
            return;
          }
          processedPCPEvents.current.add(eventId);
          pcpEventDebounceTimers.current.delete(eventId);

          const message = data?.message || 'PCP upload completed with validation errors.';
          const errorCount = data?.validation?.errorCount ?? 0;
          const errorSheetUrl = data?.errorSheet?.url;

          if (data?.status === 'validation_failed' && errorCount > 0) {
            toasterMsg(errorCount > 0 ? `Sheet has ${errorCount} error(s). Download the error sheet, fix issues, and re-upload.` : message, 'danger');
            if (errorSheetUrl) {
              downloadErrorSheet(errorSheetUrl);
            }
          } else if (data?.status === 'success') {
            toasterMsg(message || 'PCP file uploaded successfully', 'success');
          }
        }, 300);

        pcpEventDebounceTimers.current.set(eventId, debounceTimer);
      };

      newSocket.off('pcp-upload-processed').on('pcp-upload-processed', handlePCPUploadProcessed);
      newSocket.off('pcp-upload-product-processed').on('pcp-upload-product-processed', handlePCPUploadProcessed);

      const handleRPCustomSectionUploadProcessed = (data) => {
        const currentUserId = loggedInUserInfo?.supplierUserId || loggedInUserInfo?.userId;
        const payloadUserId = data?.data?.userId || data?.userId;
        if (payloadUserId && currentUserId && payloadUserId.toString() !== currentUserId.toString()) {
          return;
        }

        const errorSheetKey = data?.errorSheet?.key;
        const fileKey = data?.file?.key;
        const etag = data?.file?.etag || '';
        const eventId = errorSheetKey ?? (fileKey ? `${fileKey}:${etag}` : `rp_custom_${data?.data?.sectionId ?? ''}_${Date.now()}`);

        if (processedRPCustomSectionEvents.current.has(eventId)) {
          return;
        }

        if (rpCustomSectionEventDebounceTimers.current.has(eventId)) {
          clearTimeout(rpCustomSectionEventDebounceTimers.current.get(eventId));
        }

        const debounceTimer = setTimeout(() => {
          if (processedRPCustomSectionEvents.current.has(eventId)) {
            rpCustomSectionEventDebounceTimers.current.delete(eventId);
            return;
          }
          processedRPCustomSectionEvents.current.add(eventId);
          rpCustomSectionEventDebounceTimers.current.delete(eventId);

          offerRangePlanResult('rpCustomSection', 'rp-custom-section-upload-processed', data);
        }, 300);

        rpCustomSectionEventDebounceTimers.current.set(eventId, debounceTimer);
      };

      newSocket.off('rp-custom-section-upload-processed').on('rp-custom-section-upload-processed', handleRPCustomSectionUploadProcessed);

      const handleRangePlanCareUploadProcessed = (data) => {
        const currentUserId = loggedInUserInfo?.supplierUserId || loggedInUserInfo?.userId;
        const payloadUserId = data?.data?.userId || data?.userId;
        if (payloadUserId && currentUserId && payloadUserId.toString() !== currentUserId.toString()) {
          return;
        }

        const incomingSessionId = data?.uploadSessionId;
        if (incomingSessionId && !pendingCareUploadSessions.has(incomingSessionId)) {
          return;
        }

        const errorSheetUrl = data?.errorSheet?.url;
        const errorSheetKey = data?.errorSheet?.key;
        const fileKey = data?.file?.key;
        // Include errorSheetUrl so that a fresh Lambda run (new presigned URL)
        // is always treated as a new event, even if the source key is identical.
        const eventId = errorSheetUrl ?? errorSheetKey ?? fileKey ?? `rp_care_${data?.data?.companyURL ?? ''}_${Date.now()}`;

        if (processedRPCareUploadEvents.current.has(eventId)) {
          return;
        }

        if (rangePlanCareUploadEventDebounceTimers.current.has(eventId)) {
          clearTimeout(rangePlanCareUploadEventDebounceTimers.current.get(eventId));
        }

        const debounceTimer = setTimeout(() => {
          if (processedRPCareUploadEvents.current.has(eventId)) {
            rangePlanCareUploadEventDebounceTimers.current.delete(eventId);
            return;
          }
          processedRPCareUploadEvents.current.add(eventId);
          rangePlanCareUploadEventDebounceTimers.current.delete(eventId);

          // in case socket is reconnected, we drop the cached events
          if (incomingSessionId && pendingCareUploadSessions.delete(incomingSessionId)) {
            persistPendingCareUploads();
            try {
              newSocket.emit('care-upload-replay-ack', incomingSessionId);
            } catch (error) {
              console.error('Failed to emit care-upload-replay-ack:', error);
              // Socket may be disconnecting — TTL handles cleanup.
            }
          }

          offerRangePlanResult('rangePlanCare', 'range-plan-care-upload-processed', data);
        }, 300);

        rangePlanCareUploadEventDebounceTimers.current.set(eventId, debounceTimer);
      };

      newSocket.off('range-plan-care-upload-processed').on('range-plan-care-upload-processed', handleRangePlanCareUploadProcessed);

      const handleRangePlanTestingUploadProcessed = (data) => {
        const currentUserId = loggedInUserInfo?.supplierUserId || loggedInUserInfo?.userId;
        const payloadUserId = data?.data?.userId || data?.userId;
        if (payloadUserId && currentUserId && payloadUserId.toString() !== currentUserId.toString()) {
          return;
        }

        const incomingSessionId = data?.uploadSessionId;
        if (incomingSessionId && !pendingTestingUploadSessions.has(incomingSessionId)) {
          return;
        }

        const eventId = data?.errorSheet?.url ?? data?.errorSheet?.key ?? data?.file?.key ?? `rp_testing_${Date.now()}`;

        if (rangePlanTestingUploadEventDebounceTimers.current.has(eventId)) {
          clearTimeout(rangePlanTestingUploadEventDebounceTimers.current.get(eventId));
        }

        const debounceTimer = setTimeout(() => {
          rangePlanTestingUploadEventDebounceTimers.current.delete(eventId);
          if (incomingSessionId && pendingTestingUploadSessions.delete(incomingSessionId)) {
            try {
              sessionStorage.setItem(PENDING_TESTING_KEY, JSON.stringify(Array.from(pendingTestingUploadSessions.entries())));
              newSocket.emit('testing-upload-replay-ack', incomingSessionId);
            } catch (error) {
              console.error('Failed to emit testing-upload-replay-ack:', error);
            }
          }

          offerRangePlanResult('rangePlanTesting', 'range-plan-testing-upload-processed', data);
        }, 300);

        rangePlanTestingUploadEventDebounceTimers.current.set(eventId, debounceTimer);
      };

      newSocket.off('range-plan-testing-upload-processed').on('range-plan-testing-upload-processed', handleRangePlanTestingUploadProcessed);

      /**
       * Handles PO upload validation errors with debouncing and duplicate prevention
       * Creates stable event IDs to prevent duplicate modal displays
       * If no duplicates exist, automatically downloads error sheet without showing modal
       */
      newSocket.off('po-upload-errors').on('po-upload-errors', (data) => {
        const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserInfo'));
        const currentUserId = loggedInUserInfo?.supplierUserId || loggedInUserInfo?.userId;
        const payloadUserId = data?.data?.userId || data?.userId;

        // CRITICAL: Filter events to only show for the user who uploaded the file
        if (payloadUserId && currentUserId && payloadUserId.toString() !== currentUserId.toString()) {
          console.log('[po-upload-errors] ⏭️ Event not for current user, skipping:', {
            currentUserId,
            payloadUserId,
            status: data?.status,
          });
          return;
        }

        const fileKey = data?.file?.key;
        const updateKey = data?.file?.updateKey;
        const etag = data?.file?.etag || '';
        const eventId = updateKey ? `${updateKey}:${etag}` : fileKey ? `${fileKey}:${etag}` : null;
        if (!eventId) {
          console.warn('[po-upload-errors] No eventId generated, skipping');
          return;
        }

        if (processedPOEvents.current.has(eventId)) {
          console.log('[po-upload-errors] Event already processed, skipping:', eventId);
          return;
        }

        if (isPOModalShowing.current) {
          console.log('[po-upload-errors] Modal already showing, skipping:', eventId);
          processedPOEvents.current.add(eventId);
          return;
        }

        if (modalJustClosed.current) {
          console.log('[po-upload-errors] Modal just closed, skipping:', eventId);
          processedPOEvents.current.add(eventId);
          return;
        }

        if (poEventDebounceTimers.current.has(eventId)) {
          clearTimeout(poEventDebounceTimers.current.get(eventId));
        }

        const debounceTimer = setTimeout(() => {
          if (processedPOEvents.current.has(eventId)) {
            poEventDebounceTimers.current.delete(eventId);
            return;
          }

          if (isPOModalShowing.current) {
            processedPOEvents.current.add(eventId);
            poEventDebounceTimers.current.delete(eventId);
            return;
          }

          if (modalJustClosed.current) {
            processedPOEvents.current.add(eventId);
            poEventDebounceTimers.current.delete(eventId);
            return;
          }

          processedPOEvents.current.add(eventId);
          poEventDebounceTimers.current.delete(eventId);

          const message = data?.message || tPO('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.VALIDATION_FAILED');
          const errorCount = data?.validation?.errorCount || 0;
          const validationMessage = `${message} (${errorCount} ${tPO('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.ERRORS')})`;

          const hasDuplicatesInPayload =
            (data?.validation?.duplicateCount || 0) > 0 ||
            (data?.validation?.uniqueDuplicatePORefCount || 0) > 0 ||
            (data?.validation?.duplicates && data?.validation?.duplicates.length > 0);
          const hasValidationErrors = (data?.validation?.errorCount || 0) > 0;
          const hasDatabaseErrors = (data?.database?.failedCount || 0) > 0 || (data?.database?.errors?.length || 0) > 0;
          const hasAnyErrors = hasValidationErrors || hasDatabaseErrors;
          const errorSheetUrl = data?.errorSheet?.url;

          if (!hasDuplicatesInPayload && hasAnyErrors) {
            if (errorSheetUrl) {
              downloadErrorSheet(errorSheetUrl);
            }
            toasterMsg('Sheet has some error(s) update the sheet and re upload', 'danger');
            return;
          }

          isPOModalShowing.current = true;
          setPoDuplicateModal({
            show: true,
            data: {
              ...data,
              isValidationError: true,
              validationMessage,
            },
          });
        }, 300);

        poEventDebounceTimers.current.set(eventId, debounceTimer);
      });

      /**
       * Handles PO upload duplicate detection with debouncing and duplicate prevention
       * Uses stable event IDs to prevent duplicate modal displays
       */
      newSocket.off('po-upload-duplicates').on('po-upload-duplicates', (data) => {
        const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserInfo'));
        const currentUserId = loggedInUserInfo?.supplierUserId || loggedInUserInfo?.userId;
        const payloadUserId = data?.data?.userId || data?.userId;

        // CRITICAL: Filter events to only show for the user who uploaded the file
        // This prevents showing duplicate modals to other users when company room broadcast is used
        if (payloadUserId && currentUserId && payloadUserId.toString() !== currentUserId.toString()) {
          console.log('[po-upload-duplicates] ⏭️ Event not for current user, skipping:', {
            currentUserId,
            payloadUserId,
            status: data?.status,
          });
          return;
        }

        console.log('[po-upload-duplicates] 📥 Received duplicate notification:', {
          status: data?.status,
          hasErrorSheet: !!data?.errorSheet,
          errorSheetKey: data?.errorSheet?.key || null,
          errorSheetUrl: data?.errorSheet?.url ? 'present' : 'missing',
          duplicateCount: data?.validation?.duplicateCount || 0,
          validationErrorCount: data?.validation?.errorCount || 0,
          currentUserId,
          payloadUserId,
          userIdMatch: payloadUserId?.toString() === currentUserId?.toString(),
        });

        const updateKey = data?.file?.updateKey;
        const fileKey = data?.file?.key;
        const etag = data?.file?.etag || '';
        const eventId = updateKey ? `${updateKey}:${etag}` : fileKey ? `${fileKey}:${etag}` : null;

        if (!eventId) {
          console.warn('[po-upload-duplicates] ⚠️ No eventId generated, skipping');
          return;
        }

        if (processedPOEvents.current.has(eventId)) {
          console.log('[po-upload-duplicates] ⏭️ Event already processed, skipping:', eventId);
          return;
        }

        if (isPOModalShowing.current) {
          console.log('[po-upload-duplicates] ⏭️ Modal already showing, skipping:', eventId);
          processedPOEvents.current.add(eventId);
          return;
        }

        if (modalJustClosed.current) {
          console.log('[po-upload-duplicates] ⏭️ Modal just closed, skipping:', eventId);
          processedPOEvents.current.add(eventId);
          return;
        }

        if (poEventDebounceTimers.current.has(eventId)) {
          clearTimeout(poEventDebounceTimers.current.get(eventId));
        }

        const debounceTimer = setTimeout(() => {
          if (processedPOEvents.current.has(eventId)) {
            poEventDebounceTimers.current.delete(eventId);
            return;
          }

          if (isPOModalShowing.current) {
            console.log('[po-upload-duplicates] ⏭️ Modal showing during debounce, skipping:', eventId);
            processedPOEvents.current.add(eventId);
            poEventDebounceTimers.current.delete(eventId);
            return;
          }

          if (modalJustClosed.current) {
            console.log('[po-upload-duplicates] ⏭️ Modal just closed during debounce, skipping:', eventId);
            processedPOEvents.current.add(eventId);
            poEventDebounceTimers.current.delete(eventId);
            return;
          }

          poEventDebounceTimers.current.delete(eventId);

          const hasErrorSheet = !!data?.errorSheet?.url;
          const duplicateCount = data?.validation?.duplicateCount || data?.validation?.uniqueDuplicatePORefCount || 0;

          console.log('[po-upload-duplicates] ✅ Showing duplicate modal:', {
            eventId,
            hasErrorSheet,
            errorSheetUrl: data?.errorSheet?.url || null,
            errorSheetKey: data?.errorSheet?.key || null,
            duplicateCount,
            status: data?.status,
          });

          // CRITICAL: Show modal even if errorSheet is missing - user needs to see duplicates
          if (!hasErrorSheet && duplicateCount > 0) {
            console.warn('[po-upload-duplicates] ⚠️ WARNING: Duplicates found but errorSheet URL is missing!', {
              eventId,
              duplicateCount,
              errorSheetKey: data?.errorSheet?.key || null,
              userImpact: 'Modal will show but error sheet download may not work',
            });
          }

          isPOModalShowing.current = true;
          const hasValidationErrorsInDuplicatePayload = (data?.validation?.errorCount || 0) > 0;
          const errorCount = data?.validation?.errorCount || 0;
          const baseMessage = data?.message || '';
          const combinedValidationMessage = hasValidationErrorsInDuplicatePayload
            ? `${baseMessage} Also, ${errorCount} row(s) have validation error(s) — download error sheet to review.`
            : null;

          setPoDuplicateModal({
            show: true,
            data: {
              ...data,
              isValidationError: hasValidationErrorsInDuplicatePayload,
              validationMessage: combinedValidationMessage,
            },
          });
        }, 300);

        poEventDebounceTimers.current.set(eventId, debounceTimer);
      });

      newSocket.off('po-modal-dismissed').on('po-modal-dismissed', (data) => {
        const eventId = data?.eventId;
        const action = data?.action || 'unknown';
        if (!eventId) return;

        processedPOEvents.current.add(eventId);
        if (poEventDebounceTimers.current.has(eventId)) {
          clearTimeout(poEventDebounceTimers.current.get(eventId));
          poEventDebounceTimers.current.delete(eventId);
        }

        if (poDuplicateModal.show && poDuplicateModal.data) {
          const modalFileInfo = poDuplicateModal.data?.file;
          const modalUpdateKey = modalFileInfo?.updateKey;
          const modalFileKey = modalFileInfo?.key;
          const modalEtag = modalFileInfo?.etag || '';
          const modalEventId = modalUpdateKey ? `${modalUpdateKey}:${modalEtag}` : modalFileKey ? `${modalFileKey}:${modalEtag}` : null;

          if (modalEventId === eventId) {
            isPOModalShowing.current = false;
            modalJustClosed.current = true;
            setPoDuplicateModal({ show: false, data: null });
            setTimeout(() => {
              modalJustClosed.current = false;
            }, 1000);
          }
        }
      });

      newSocket.off('po-update-complete').on('po-update-complete', (data) => {
        const updateKey = data?.file?.updateKey;
        const fileKey = data?.file?.key;
        const etag = data?.file?.etag || '';
        const eventId = updateKey ? `${updateKey}:${etag}` : fileKey ? `${fileKey}:${etag}` : null;

        if (eventId) {
          processedPOEvents.current.add(eventId);
          if (poEventDebounceTimers.current.has(eventId)) {
            clearTimeout(poEventDebounceTimers.current.get(eventId));
            poEventDebounceTimers.current.delete(eventId);
          }
        }

        const database = data?.database;
        const updatedCount = database?.updatedIds?.length || 0;
        const insertedCount = database?.insertedIds?.length || 0;
        const operation = database?.operation || 'process';
        const fileName = data?.file?.filename || 'file';
        const errorSheetUrl = data?.errorSheet?.url;
        const hasValidationErrors = (data?.validation?.errorCount || 0) > 0;

        let notificationMessage = '';

        if (data?.message) {
          notificationMessage = data.message;
        }

        if (hasValidationErrors && errorSheetUrl) {
          downloadErrorSheet(errorSheetUrl);
          toasterMsg('Some rows had errors - error sheet downloaded.', 'warning');
        } else {
          toasterMsg(notificationMessage, 'success');
        }

        const isOnTodoPage = window.location.pathname.includes('/todo-list/product-purchase-order-todo');
        if (isOnTodoPage) {
          window.dispatchEvent(new CustomEvent('po-list-refresh'));
        }
      });

      newSocket.off('po-operation-failed').on('po-operation-failed', (data) => {
        const message = data?.message || tPO('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.OPERATION_FAILED');
        toasterMsg(message, 'danger');
      });

      newSocket.off('pim-mapping-status-reverted').on('pim-mapping-status-reverted', (data) => {
        if (!data?.channelSetupId) return;
        window.dispatchEvent(new CustomEvent('pim:mapping-status-reverted', { detail: data }));
      });

      const isEventForCurrentUser = (data) => {
        const currentUserId = loggedInUserInfo?.supplierUserId || loggedInUserInfo?.userId;
        const payloadUserId = data?.data?.userId || data?.userId;
        return !payloadUserId || !currentUserId || payloadUserId.toString() === currentUserId.toString();
      };

      newSocket.off('supplier-upload-processing').on('supplier-upload-processing', (data) => {
        if (!isEventForCurrentUser(data)) return;
        toasterMsg(tSupplier('SUPPLIER_OVERVIEW.UPLOADING_SUPPLIER_FILE'), 'info');
      });

      newSocket.off('supplier-upload-validating').on('supplier-upload-validating', (data) => {
        if (!isEventForCurrentUser(data)) return;
        toasterMsg(tSupplier('SUPPLIER_OVERVIEW.UPLOAD_VALIDATING'), 'info');
      });

      newSocket.off('supplier-upload-success').on('supplier-upload-success', (data) => {
        if (!isEventForCurrentUser(data)) return;
        const message = data?.message || tSupplier('SUPPLIER_OVERVIEW.UPLOAD_SUCCESS');
        toasterMsg(message, 'success');
        window.dispatchEvent(new CustomEvent('supplier-list-refresh'));
      });

      newSocket.off('supplier-upload-errors').on('supplier-upload-errors', (data) => {
        if (!isEventForCurrentUser(data)) return;
        const errorCount = data?.validation?.errorCount ?? 0;
        const message = data?.message || tSupplier('SUPPLIER_OVERVIEW.UPLOAD_FAILED');
        if (errorCount > 0) {
          setSupplierErrorModal({ show: true, data });
          // Refresh list when some rows were saved alongside the errors
          const isOnSupplierPage = window.location.pathname.includes('/suppliers');
          if (isOnSupplierPage && (data?.database?.modifiedCount ?? 0) > 0) {
            window.dispatchEvent(new CustomEvent('supplier-list-refresh'));
          }
        } else {
          toasterMsg(message, 'danger');
        }
      });

      newSocket.off('supplier-upload-duplicates').on('supplier-upload-duplicates', (data) => {
        if (!isEventForCurrentUser(data)) return;
        setSupplierDuplicateModal({ show: true, data });
      });

      newSocket.off('supplier-upload-failed').on('supplier-upload-failed', (data) => {
        if (!isEventForCurrentUser(data)) return;
        const message = data?.message || tSupplier('SUPPLIER_OVERVIEW.UPLOAD_FAILED');
        toasterMsg(message, 'danger');
      });
    }
  }, []);

  /**
   * Handle modal hide/cancel action
   * Closes modal and notifies other tabs/devices via socket
   */
  const handleModalHide = () => {
    const fileInfo = poDuplicateModal.data?.file;
    const companyURL = poDuplicateModal.data?.data?.companyURL;

    if (fileInfo) {
      const updateKey = fileInfo?.updateKey;
      const fileKey = fileInfo?.key;
      const etag = fileInfo?.etag || '';
      const eventId = updateKey ? `${updateKey}:${etag}` : fileKey ? `${fileKey}:${etag}` : null;

      if (eventId) {
        if (processedPOEvents.current.has(eventId)) {
          isPOModalShowing.current = false;
          modalJustClosed.current = true;
          setPoDuplicateModal({ show: false, data: null });
          setTimeout(() => {
            modalJustClosed.current = false;
          }, 1000);
          return;
        }

        processedPOEvents.current.add(eventId);
        if (poEventDebounceTimers.current.has(eventId)) {
          clearTimeout(poEventDebounceTimers.current.get(eventId));
          poEventDebounceTimers.current.delete(eventId);
        }
        if (socketState?.socketContext && companyURL) {
          socketState.socketContext.emit('po-modal-dismissed', {
            eventId,
            companyURL,
            action: 'cancel',
          });
        }
      }
    }

    isPOModalShowing.current = false;
    modalJustClosed.current = true;
    setPoDuplicateModal({ show: false, data: null });
    setTimeout(() => {
      modalJustClosed.current = false;
    }, 1000);
  };

  /**
   * Handle modal confirm action
   * Processes file based on whether it has duplicates or validation errors
   */
  const handleModalConfirm = () => {
    const modalData = poDuplicateModal.data;
    const fileInfo = modalData?.file;
    const companyURL = modalData?.data?.companyURL;
    const errorSheetKey = modalData?.errorSheet?.key;
    const duplicateCount = modalData?.validation?.duplicateCount || 0;
    const duplicatesArray = modalData?.validation?.duplicates || [];
    const hasDuplicates = duplicateCount > 0 || duplicatesArray.length > 0;

    const updateKey = fileInfo?.updateKey;
    const fileKey = fileInfo?.key;
    const etag = fileInfo?.etag || '';
    const eventId = updateKey ? `${updateKey}:${etag}` : fileKey ? `${fileKey}:${etag}` : null;

    if (eventId && processedPOEvents.current.has(eventId)) {
      isPOModalShowing.current = false;
      modalJustClosed.current = true;
      setPoDuplicateModal({ show: false, data: null });
      setTimeout(() => {
        modalJustClosed.current = false;
      }, 1000);
      return;
    }

    isPOModalShowing.current = false;
    modalJustClosed.current = true;
    setPoDuplicateModal({ show: false, data: null });
    setTimeout(() => {
      modalJustClosed.current = false;
    }, 1000);

    if (eventId && socketState?.socketContext && companyURL) {
      socketState.socketContext.emit('po-modal-dismissed', {
        eventId,
        companyURL,
        action: 'confirm',
      });
    }

    if (!hasDuplicates) {
      return;
    }

    if (eventId) {
      processedPOEvents.current.add(eventId);
      if (poEventDebounceTimers.current.has(eventId)) {
        clearTimeout(poEventDebounceTimers.current.get(eventId));
        poEventDebounceTimers.current.delete(eventId);
      }
    }

    if (errorSheetKey && errorSheetKey.includes('Lambda_PO_Error/')) {
      if (isMovingFile.current) {
        toasterMsg('File move already in progress. Please wait.', 'warning');
        return;
      }

      isMovingFile.current = true;
      (async () => {
        try {
          const result = await dispatch(ProductPurchaseOrderTodoAction.moveErrorSheetToUpdate({ errorSheetKey, originalKey: fileInfo?.key }, tPO));

          if (result?.code === 200) {
            toasterMsg('Purchase Order sheet is being processed.', 'success');
          }
        } catch (error) {
          console.error('[onConfirm] Error copying error sheet:', error);
          toasterMsg(`Failed to trigger update: ${error.message}`, 'danger');
        } finally {
          isMovingFile.current = false;
        }
      })();
    } else if (fileInfo?.key) {
      console.log('[onConfirm] Copying original file to /confirmed/:', fileInfo.key);
      triggerPOUpdate(fileInfo);
    } else {
      console.error('[onConfirm] No file to process');
      toasterMsg('Unable to process file. Please try uploading again.', 'danger');
    }
  };

  /**
   * Triggers PO update via Lambda API when user confirms duplicates
   * Copies file to /confirmed/ folder which triggers Lambda to process with duplicates
   * Prevents duplicate triggers using event ID tracking
   */
  const triggerPOUpdate = async (fileInfo) => {
    console.log('[triggerPOUpdate] 🔍 CALLED with fileInfo:', JSON.stringify(fileInfo, null, 2));

    if (!fileInfo?.key) {
      console.error('[triggerPOUpdate] ❌ Missing required fileInfo.key:');
      toasterMsg(tPO('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.UPDATE_TRIGGER_ERROR'), 'danger');
      return;
    }

    const fileKey = fileInfo.key;
    const etag = fileInfo?.etag || '';
    const updateKey = fileInfo?.updateKey;
    const eventId = updateKey ? `${updateKey}:${etag}` : `${fileKey}:${etag}`;

    if (lambdaTriggerInProgress.current) {
      return;
    }

    lambdaTriggerInProgress.current = true;

    try {
      const response = await fetch('/api/lambda-api/triggerPOUpdate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sourceKey: fileInfo.key,
        }),
      });

      const result = await response.json();

      if (result.code === 200) {
        if (eventId && !processedPOEvents.current.has(eventId)) {
          processedPOEvents.current.add(eventId);
        }
        if (eventId && poEventDebounceTimers.current.has(eventId)) {
          clearTimeout(poEventDebounceTimers.current.get(eventId));
          poEventDebounceTimers.current.delete(eventId);
        }
        toasterMsg('Purchase Order sheet is being processed. You will be notified once the update is complete.', 'success');
      } else {
        console.error('[triggerPOUpdate] Backend returned error:', result);
        toasterMsg(`${tPO('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.UPDATE_TRIGGER_FAILED')}: ${result.message || ''}`, 'danger');
      }
    } catch (error) {
      console.error('[triggerPOUpdate] Failed to trigger PO update:', error);
      toasterMsg(tPO('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.UPDATE_TRIGGER_ERROR'), 'danger');
    } finally {
      lambdaTriggerInProgress.current = false;
    }
  };

  /**
   * Handle supplier duplicate modal — Accept (update existing records)
   * Calls /confirmSupplierUpload which copies the S3 file to /confirmed/ and re-triggers Lambda
   */
  const handleSupplierDuplicateAccept = async () => {
    const modalData = supplierDuplicateModal.data;
    const s3Key = modalData?.file?.key;
    const bucket = modalData?.file?.bucket || modalData?.bucket;

    setSupplierDuplicateModal({ show: false, data: null });

    if (!s3Key) {
      toasterMsg('Unable to confirm upload — file key is missing. Please re-upload.', 'danger');
      return;
    }

    if (isConfirmingSupplierUpload.current) {
      return;
    }

    isConfirmingSupplierUpload.current = true;

    try {
      const body = { s3Key };
      if (bucket) body.bucket = bucket;
      const response = await fetch('/api/lambda-api/confirmSupplierUpload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (result.code === 200) {
        toasterMsg(result.message || tSupplier('SUPPLIER_OVERVIEW.UPLOAD_CONFIRMED'), 'info');
      } else {
        toasterMsg(`Failed to confirm supplier upload: ${result.message || ''}`, 'danger');
      }
    } catch (error) {
      console.error('[handleSupplierDuplicateAccept] Error:', error);
      toasterMsg('Failed to confirm supplier upload. Please try again.', 'danger');
    } finally {
      isConfirmingSupplierUpload.current = false;
    }
  };

  /**
   * Handle supplier duplicate modal — Cancel (download error sheet with duplicate row info)
   */
  const handleSupplierDuplicateCancel = async () => {
    const errorSheetUrl = supplierDuplicateModal.data?.errorSheet?.url;
    setSupplierDuplicateModal({ show: false, data: null });
    if (errorSheetUrl) {
      await downloadErrorSheet(errorSheetUrl);
    }
  };

  /**
   * Handle RangePlan duplicate modal cancel/download action
   * Downloads error file and closes modal
   */
  const handleRangePlanDuplicateCancel = async () => {
    try {
      if (dupData?.file) {
        const response = await fetch(dupData.file);
        const url = new URL(response.url);
        const filename = url.pathname.split('/').pop();
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (e) {
      console.error('Download error:', e);
    }
    setDupModalOpen(false);
    setDupData(null);
  };

  /**
   * Handle RangePlan duplicate modal confirm action
   * Confirms including duplicates and continues upload. When the same run also found key-field
   * changes on those duplicate rows (BM-9211), the key-field popup opens straight away instead and
   * the upload re-runs once, after that popup is answered.
   */
  const handleRangePlanDuplicateConfirm = async () => {
    const keyFieldStep = resolveKeyFieldConfirmationAfterDuplicates(dupData);
    if (keyFieldStep) {
      setDupModalOpen(false);
      setDupData(null);
      setKeyFieldConfirmation(keyFieldStep);
      return;
    }
    if (dupData?.uploadKey) {
      rememberApprovedDuplicates(sharedTabStorage(), { uploadKey: dupData.uploadKey, file: dupData.file }, Date.now());
    }
    try {
      await dispatch(
        ProductListAction.confirmIncludeDuplicatesUpload({
          uploadKey: dupData?.uploadKey,
          activeTab: 'product-info',
        }),
      );
      setDupModalOpen(false);
      setDupData(null);
      toasterMsg(t('RANGE_PLANS.RP_UPLOAD_CONTINUING') || 'Continuing upload...', 'success');
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    isPOModalShowing.current = poDuplicateModal.show;
  }, [poDuplicateModal.show]);

  useEffect(() => {
    return () => {
      socketState?.socketContext?.off('connect');
      socketState?.socketContext?.off('disconnect');
      socketState?.socketContext?.off('pong');
      socketState?.socketContext?.off('product-listing-custom-section-success');
      socketState?.socketContext?.off('reconnect');
      socketState?.socketContext?.off('connect_error');
      socketState?.socketContext?.off('lambda-callback-success');
      socketState?.socketContext?.off('rp-default-options-completed');
      socketState?.socketContext?.off('rp-identifier-barcodes-completed');
      socketState?.socketContext?.off('todo-pdf-ready');
      socketState?.socketContext?.off('submit-download-ready');
      socketState?.socketContext?.off('po-upload-processing');
      socketState?.socketContext?.off('po-upload-success');
      socketState?.socketContext?.off('pcp-upload-processed');
      socketState?.socketContext?.off('pcp-upload-product-processed');
      socketState?.socketContext?.off('rp-custom-section-upload-processed');
      socketState?.socketContext?.off('range-plan-care-upload-processed');
      socketState?.socketContext?.off('range-plan-testing-upload-processed');
      socketState?.socketContext?.off('po-upload-errors');
      socketState?.socketContext?.off('po-upload-duplicates');
      socketState?.socketContext?.off('po-update-complete');
      socketState?.socketContext?.off('po-operation-failed');
      socketState?.socketContext?.off('po-modal-dismissed');
      socketState?.socketContext?.off('pim-mapping-status-reverted');
      socketState?.socketContext?.off('supplier-upload-processing');
      socketState?.socketContext?.off('supplier-upload-validating');
      socketState?.socketContext?.off('supplier-upload-success');
      socketState?.socketContext?.off('supplier-upload-errors');
      socketState?.socketContext?.off('supplier-upload-duplicates');
      socketState?.socketContext?.off('supplier-upload-failed');
      poEventDebounceTimers.current.forEach((timer) => clearTimeout(timer));
      poEventDebounceTimers.current.clear();
      pcpEventDebounceTimers.current.forEach((timer) => clearTimeout(timer));
      pcpEventDebounceTimers.current.clear();
      rpCustomSectionEventDebounceTimers.current.forEach((timer) => clearTimeout(timer));
      rpCustomSectionEventDebounceTimers.current.clear();
      rangePlanCareUploadEventDebounceTimers.current.forEach((timer) => clearTimeout(timer));
      rangePlanCareUploadEventDebounceTimers.current.clear();
      rangePlanTestingUploadEventDebounceTimers.current.forEach((timer) => clearTimeout(timer));
      rangePlanTestingUploadEventDebounceTimers.current.clear();
      isPOModalShowing.current = false;
    };
  }, [socketState?.socketContext]);

  const contextValue = useMemo(
    () => ({
      socketState,
      setSocket,
    }),
    [socketState, setSocket],
  );

  /**
   * Downloads a Range Plan upload's error sheet from its signed URL.
   * @param {string} fileUrl - Signed S3 URL of the error sheet.
   */
  const downloadRangePlanErrorSheet = async (fileUrl) => {
    try {
      const response = await fetch(fileUrl);
      const url = new URL(response.url);
      const filename = url.pathname.split('/').pop();
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error('Download error:', e);
    }
  };

  /**
   * Confirms the key-field changes a held Product Information upload found and re-runs it
   * (BM-9211). The duplicates decision already made is carried over unchanged.
   */
  const handleRangePlanKeyFieldConfirm = async () => {
    const pending = keyFieldConfirmation;
    setKeyFieldConfirmation(null);
    if (pending) forgetApprovedDuplicates(sharedTabStorage(), pending.uploadKey);
    if (!pending) return;
    try {
      await dispatch(ProductListAction.confirmIncludeDuplicatesUpload(buildKeyFieldConfirmPayload(pending)));
      toasterMsg(t('RANGE_PLANS.RP_UPLOAD_CONTINUING') || 'Continuing upload...', 'success');
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Abandons a held Product Information upload (BM-9211). Nothing has been written — not even the
   * duplicates the user approved one step earlier — so the upload ends as a duplicates rejection
   * would: that step's error sheet, which lists the duplicate rows, is downloaded, falling back to
   * this run's own sheet when there were no duplicates. The toast says nothing was saved either way.
   */
  const handleRangePlanKeyFieldCancel = async () => {
    const pending = keyFieldConfirmation;
    setKeyFieldConfirmation(null);
    if (pending) forgetApprovedDuplicates(sharedTabStorage(), pending.uploadKey);
    const errorSheet = resolveCancelErrorSheet(pending);
    if (errorSheet) {
      await downloadRangePlanErrorSheet(errorSheet);
    }
    toasterMsg(t('product-information:RANGE_PLAN_UPLOAD_CANCELLED'), 'warning');
  };

  /**
   * `localStorage`, shared by every tab of the app, or null when the browser blocks it — the helpers
   * that take it treat null as "nothing remembered".
   * @returns {Storage|null} The shared storage.
   */
  const sharedTabStorage = () => {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  };

  /**
   * Shares a Range Plan upload result with the user's other tabs, so that exactly one of them — the
   * one being looked at — shows it (BM-9211). Before the router has started it is shown here.
   * @param {string} kind - Which presenter shows it.
   * @param {string} eventName - The socket event it arrived on.
   * @param {Object} data - The socket payload.
   */
  const offerRangePlanResult = (kind, eventName, data) => {
    const router = rangePlanResultRouterRef.current;
    if (!router) {
      rangePlanResultPresenterRef.current?.(kind, data);
      return;
    }
    router
      .offer({ eventId: resolveResultEventId(eventName, data), kind, payload: data })
      .catch((error) => console.error('Range plan result routing failed:', error));
  };

  /**
   * Shows one Range Plan upload result in this tab — the tab the user is looking at (BM-9211). This is
   * the UI each socket handler used to run straight away; the handlers now only receive and filter.
   * @param {string} kind - Which result it is.
   * @param {Object} data - The socket payload.
   */
  const presentRangePlanResult = (kind, data) => {
    switch (kind) {
      case 'lambdaCallback': {
        const file = data?.data?.file;
        const errorMsg = data?.data?.errorMsg;
        const duplicatesFound = data?.data?.duplicatesFound;
        const uploadKey = data?.data?.uploadKey;

        if (errorMsg) {
          toasterMsg(errorMsg, 'danger');
          return;
        }
        if (duplicatesFound && uploadKey) {
          setDupData({ file, uploadKey, keyFieldChangesFound: data?.data?.keyFieldChangesFound === true });
          setDupModalOpen(true);
          return;
        }
        if (resolveUploadPrompt(data?.data) === 'keyFieldChanges') {
          setKeyFieldConfirmation(
            buildKeyFieldConfirmation(
              { uploadKey, file, includeDuplicates: data?.data?.includeDuplicates === true },
              readApprovedDuplicates(sharedTabStorage(), uploadKey, Date.now()),
            ),
          );
          return;
        }
        if (file) {
          downloadRangePlanErrorSheet(file);
          toasterMsg('Rangeplan sheet have some invalid data. Please check the error sheet.', 'danger');
        } else {
          toasterMsg('Rangeplan sheet upload completed', 'success');
        }
        return;
      }
      case 'rpDefaultOptions': {
        if (data?.status === 'error') {
          toasterMsg('Failed to apply range plan default options', 'danger');
        } else if (data?.status === 'success' && data?.applied && data?.devOrderCount > 0) {
          toasterMsg(`Range plan defaults applied to ${data.devOrderCount} product(s)`, 'success');
        }
        return;
      }
      case 'rpIdentifierBarcodes': {
        presentIdentifierBarcodesResult(data);
        return;
      }
      case 'rpCustomSection': {
        const message = data?.message;
        const errorCount = data?.validation?.errorCount ?? 0;
        const errorSheetUrl = data?.errorSheet?.url;

        if (data?.status === 'validation_failed') {
          if (errorSheetUrl) {
            const toastText =
              errorCount > 0
                ? t('RANGE_PLANS.SOCKET.RP_CUSTOM_SECTION_VALIDATION_ERRORS', { count: errorCount })
                : message || t('RANGE_PLANS.SOCKET.RP_CUSTOM_SECTION_VALIDATION_GENERIC');
            toasterMsg(toastText, 'danger');
            downloadErrorSheet(errorSheetUrl);
          } else {
            toasterMsg(message || t('RANGE_PLANS.SOCKET.RP_CUSTOM_SECTION_VALIDATION_GENERIC'), 'danger');
          }
        } else if (data?.status === 'success') {
          toasterMsg(message || t('RANGE_PLANS.SOCKET.RP_CUSTOM_SECTION_SUCCESS'), 'success');
        }
        return;
      }
      case 'rangePlanCare': {
        const status = data?.status;
        const message = data?.message;
        const failedRowCount = data?.summary?.failedRowCount ?? 0;
        const errorSheetUrl = data?.errorSheet?.url;
        const NON_SUCCESS_TOAST_MS = 6000;

        if (status === 'success') {
          toasterMsg(message || 'Care instructions uploaded successfully', 'success');
          return;
        }
        /** The toast goes out BEFORE the download starts, so the download bar cannot hide the status. */
        if (status === 'partial_success') {
          toasterMsg(message || `Care upload completed with ${failedRowCount} error row(s). Error sheet downloaded.`, 'warning', NON_SUCCESS_TOAST_MS);
        } else {
          toasterMsg(message || 'Care upload failed. Please review the error sheet and re-upload.', 'danger', NON_SUCCESS_TOAST_MS);
        }
        if (errorSheetUrl) {
          downloadCareErrorSheet(errorSheetUrl);
        }
        return;
      }
      case 'rangePlanTesting': {
        if (data?.status === 'success') {
          toasterMsg(data?.message || 'Testing uploaded successfully', 'success');
          return;
        }
        toasterMsg(data?.message || 'Testing upload completed with issues.', data?.status === 'partial_success' ? 'warning' : 'danger', 6000);
        if (data?.errorSheet?.url) {
          downloadCareErrorSheet(data.errorSheet.url);
        }
        return;
      }
      default:
        return;
    }
  };

  /** Keeps the router pointed at this render's presenter, so a shown result never reads stale state. */
  useEffect(() => {
    rangePlanResultPresenterRef.current = presentRangePlanResult;
  });

  /** Starts the one-tab router for Range Plan upload results, once per tab (BM-9211). */
  useEffect(() => {
    const router = startFocusedTabResultRouter((kind, payload) => rangePlanResultPresenterRef.current?.(kind, payload));
    rangePlanResultRouterRef.current = router;
    return () => {
      router.stop();
      rangePlanResultRouterRef.current = null;
    };
  }, []);

  const presentIdentifierBarcodesResult = (data) => {
    const result = data?.result ?? {};
    const tr = (key, options) => tRangePlanIdentifier(`RANGE_PLAN_PRODUCT_IDENTIFIER.${key}`, options);
    if (data?.status !== 'success') {
      toasterMsg(tr('ACTION_FAILED'), 'danger');
      return;
    }
    const doneKey = result.kind === 'gtin14' ? 'GENERATE_DONE' : 'ASSIGN_DONE';
    toasterMsg(tr(doneKey, { claimed: result.claimed ?? 0, products: result.productsUpdated ?? 0 }), 'success');
    if (result.shortfall > 0) toasterMsg(tr('ASSIGN_SHORTFALL', { count: result.shortfall }), 'warning');
    if (result.skippedNoCard > 0) toasterMsg(tr('SKIPPED_NO_CARD', { count: result.skippedNoCard }), 'warning');
    if (result.skippedNoBase > 0 || result.skippedNoIndicator > 0) {
      toasterMsg(tr('GENERATE_SKIPPED', { noBase: result.skippedNoBase ?? 0, noIndicator: result.skippedNoIndicator ?? 0 }), 'warning');
    }
    if (result.conflicts > 0) toasterMsg(tr('GENERATE_CONFLICTS', { count: result.conflicts }), 'warning');
  };
  const rangePlanDuplicateConfirmLock = useRef(false);
  const [isConfirmingRangePlanDuplicates, setIsConfirmingRangePlanDuplicates] = useState(false);
  const rangePlanKeyFieldConfirmLock = useRef(false);
  const [isConfirmingRangePlanKeyFields, setIsConfirmingRangePlanKeyFields] = useState(false);

  /** Confirms the Range Plan duplicates popup once, however many times Confirm is clicked. */
  const handleRangePlanDuplicateConfirmOnce = () =>
    runSingleFlight(rangePlanDuplicateConfirmLock, handleRangePlanDuplicateConfirm, setIsConfirmingRangePlanDuplicates);

  /** Confirms the Range Plan key-field popup once, however many times Confirm is clicked. */
  const handleRangePlanKeyFieldConfirmOnce = () =>
    runSingleFlight(rangePlanKeyFieldConfirmLock, handleRangePlanKeyFieldConfirm, setIsConfirmingRangePlanKeyFields);

  return (
    <StateContext.Provider value={contextValue}>
      {children}

      {dupModalOpen && (
        <ConfirmationPopup
          onOpen={dupModalOpen}
          toggle={handleRangePlanDuplicateCancel}
          onConfirm={handleRangePlanDuplicateConfirmOnce}
          loading={isConfirmingRangePlanDuplicates}
          disabledBtn={isConfirmingRangePlanDuplicates}
          title={t('RANGE_PLANS.DUPLICATE_FOUND_CONFIRM') || 'Confirm'}
          message={t('RANGE_PLANS.DUPLICATE_FOUND_INCLUDE') || 'Duplicate rows found. Do you want to include duplicates and continue?'}
        />
      )}

      {keyFieldConfirmation && (
        <ConfirmationPopup
          onOpen={Boolean(keyFieldConfirmation)}
          toggle={handleRangePlanKeyFieldCancel}
          onConfirm={handleRangePlanKeyFieldConfirmOnce}
          loading={isConfirmingRangePlanKeyFields}
          disabledBtn={isConfirmingRangePlanKeyFields}
          title={t('product-information:PRODUCT_IDENTIFIER_CHANGE.TITLE')}
          message={t('product-information:PRODUCT_IDENTIFIER_CHANGE.MESSAGE', { variableOneName: lengthLabel })}
          secondMessage={t('product-information:PRODUCT_IDENTIFIER_CHANGE.SECOND_MESSAGE')}
        />
      )}

      <DuplicateConfirmModal
        show={poDuplicateModal.show}
        onHide={handleModalHide}
        onConfirm={handleModalConfirm}
        duplicateCount={poDuplicateModal.data?.validation?.uniqueDuplicatePORefCount || poDuplicateModal.data?.validation?.duplicateCount || 0}
        newCount={
          poDuplicateModal.data?.validation?.uniqueNewPORefCount ||
          poDuplicateModal.data?.database?.insertedIds?.length ||
          (poDuplicateModal.data?.validation?.validRows || 0) - (poDuplicateModal.data?.validation?.duplicateCount || 0)
        }
        errorSheetUrl={poDuplicateModal.data?.errorSheet?.url}
        isValidationError={poDuplicateModal.data?.isValidationError || false}
        validationMessage={poDuplicateModal.data?.validationMessage || null}
      />

      <SupplierUploadErrorModal
        show={supplierErrorModal.show}
        onHide={() => setSupplierErrorModal({ show: false, data: null })}
        onConfirm={() => setSupplierErrorModal({ show: false, data: null })}
        errorSheetUrl={supplierErrorModal.data?.errorSheet?.url}
        validationMessage={supplierErrorModal.data?.message ?? null}
      />

      <SupplierDuplicateConfirmModal
        show={supplierDuplicateModal.show}
        duplicateCount={supplierDuplicateModal.data?.validation?.duplicateCount ?? 0}
        onAccept={handleSupplierDuplicateAccept}
        onCancel={handleSupplierDuplicateCancel}
      />
    </StateContext.Provider>
  );
}

export function useSocketContextState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the SocketContextProvider');
  }
  return context;
}
